import { createElement, lazy, type ComponentType, type LazyExoticComponent } from 'react';

// Lazily loaded feature entry modules. React.lazy caches a rejected import
// forever, so a screen whose chunk failed to load (flaky network) could never
// render again without a page reload. Each screen keeps its lazy component in
// a variable that `resetFailedScreens()` replaces, and the stable wrapper
// component renders whichever instance is current.
//
// React.lazy also suspends on its first render even when the chunk is already
// loaded, and React 19 then keeps the Suspense fallback up for ~300 ms (an
// opaque canvas over the whole screen the first time Book Details or the
// player opens). Once the module has resolved, the wrapper renders its
// component directly, so a preloaded screen never suspends.

type Loader<P> = () => Promise<{ default: ComponentType<P> }>;

export interface LazyScreen<P extends object = object> {
  /** Stable component to render (inside a Suspense boundary). */
  Component: ComponentType<P>;
  /** Starts (or joins) the chunk download. */
  preload: () => Promise<unknown>;
}

/** Anything whose chunk can be warmed (every `LazyScreen`, whatever its props). */
export type Preloadable = Pick<LazyScreen, 'preload'>;

/** Resetters of screens whose last load failed. */
const failed = new Set<() => void>();

export function lazyScreen<P extends object = object>(loader: Loader<P>, name = 'LazyScreen'): LazyScreen<P> {
  let pending: Promise<{ default: ComponentType<P> }> | null = null;
  let resolved: ComponentType<P> | null = null;
  const load = () =>
    (pending ??= loader().then(
      (module) => {
        // Set before React.lazy's own handler runs, so its retry render already takes the direct path.
        resolved = module.default;
        return module;
      },
      (error: unknown) => {
        // A later preload or retry downloads again; the rendered lazy instance is replaced on Retry.
        pending = null;
        failed.add(reset);
        throw error;
      },
    ));
  let current: LazyExoticComponent<ComponentType<P>> = lazy(load);
  function reset() {
    failed.delete(reset);
    current = lazy(load);
  }
  function Screen(props: P) {
    return createElement(resolved ?? current, props);
  }
  Screen.displayName = name;
  return { Component: Screen, preload: load };
}

/** Lets every screen whose chunk failed to load try again on its next mount (error-boundary Retry). */
export function resetFailedScreens(): void {
  [...failed].forEach((reset) => reset());
}
